import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, Component } from 'react'
import GridLayout from 'react-grid-layout/legacy'
import { Loader, RefreshCw, Edit2, LayoutGrid } from 'lucide-react'
import { WidgetRenderer } from './DashboardWidgets'
import { WidgetConfigModal } from './WidgetConfigModal'
import { dashboardApi } from '../../lib/dashboardApi'

// ─── Preview data so chart widgets show their frame instead of spinner ─────────
function previewData(type) {
  const CHART = new Set(['line_chart','smooth_line_chart','combo_chart','multi_chart','area_chart','column_chart','stacked_column_chart','bar_chart'])
  const PIE   = new Set(['pie_chart','doughnut_chart','pie_chart_3d'])
  if (CHART.has(type)) return { series: [], lines: [], bars: [], areas: [] }
  if (PIE.has(type))   return { series: [] }
  if (type === 'data_table') return { rows: [], columns: [] }
  return null
}

// ─── Error boundary ───────────────────────────────────────────────────────────
class WidgetErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: false } }
  static getDerivedStateFromError() { return { err: true } }
  render() {
    if (this.state.err) return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-200 p-3 text-center">
        <span className="text-2xl mb-1">{this.props.icon || '📊'}</span>
        <p className="text-xs font-medium text-gray-500">{this.props.name}</p>
      </div>
    )
    return this.props.children
  }
}

// ─── Widget cell ──────────────────────────────────────────────────────────────
function WidgetCell({ widget, isEditing, onRemove, onConfigure }) {
  const widgetType = widget.widget_type || widget.master_widget?.widget_type || 'custom'

  // Per-instance overrides take precedence over master widget defaults
  const displayTitle   = widget.custom_title ?? widget.name
  const displayContent = widget.custom_rich_content ?? widget.rich_content ?? widget.master_widget?.rich_content ?? ''
  const w = {
    id: widget.id,
    instance_id: widget.id,
    widget_type: widgetType,
    title: displayTitle,
    description: widget.custom_description ?? widget.description ?? '',
    size_rows: widget.rows || 1,
    size_cols: widget.cols || 1,
    data_config: widget.master_widget?.default_config || {},
    rich_content: displayContent,
    master_widget_id: widget.master_widget_id || widget.master_widget?.id,
    custom_title: widget.custom_title,
    custom_description: widget.custom_description,
    custom_rich_content: widget.custom_rich_content,
    icon: widget.icon,
    is_visible: true,
    order: 0,
  }
  return (
    <div className="relative group h-full">
      {isEditing && (
          /* Remove button only — configure button removed */
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={() => onRemove(widget.id)}
            className="no-drag absolute top-1.5 right-1.5 z-20 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity leading-none"
          >✕</button>
      )}
      <WidgetErrorBoundary icon={widget.icon} name={widget.custom_title || widget.name}>
        <WidgetRenderer widget={w} data={previewData(widgetType)} isEditing={false} noWrap onConfigure={onConfigure} />
      </WidgetErrorBoundary>
    </div>
  )
}

// ─── Backend widgets → react-grid-layout items ───────────────────────────────
function toGLItems(widgets, cols) {
  return widgets.map(w => ({
    i: String(w.id),
    x: w.col  ?? 0,
    y: w.row  ?? 0,
    w: Math.min(w.cols || w.master_widget?.default_width  || 1, cols),
    h: w.rows || w.master_widget?.default_height || 1,
    minW: 1, minH: 1,
  }))
}

// ─── CSS-grid view (reliable, no width measurement needed) ───────────────────
// Responsive: collapses column count on narrow viewports so widgets stay
// legible and never overflow the container.
//   container ≥ 1024px → full layoutConfig.cols (template default)
//   container ≥  640px → min(2, baseCols)
//   container <  640px → 1 column
// When the column count is below baseCols, widgets are stack-flowed (auto
// placement, spans clamped) instead of using their stored col/row positions —
// stored positions only make sense in the original column count.
function CSSGridView({ widgets, layoutConfig, isEditing, onRemove, onConfigure }) {
  const baseCols  = layoutConfig.cols || 3
  const rowHeight = Math.max(layoutConfig.row_height || 200, 150)
  const gap       = layoutConfig.gap || 8

  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    const measure = () => setContainerWidth(Math.floor(node.getBoundingClientRect().width))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [])

  let cols = baseCols
  if (containerWidth > 0) {
    if (containerWidth < 640)       cols = 1
    else if (containerWidth < 1024) cols = Math.min(2, baseCols)
  }
  const stackMode = cols < baseCols

  return (
    <div
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: `${gap}px`,
      }}
    >
      {widgets.map(w => {
        const widgetCols = w.cols || w.master_widget?.default_width  || 1
        const rowSpan    = w.rows || w.master_widget?.default_height || 1
        const minH       = rowSpan * rowHeight + (rowSpan - 1) * gap
        const style = stackMode
          // Narrow viewport: auto-flow in source order, clamp span to available cols
          ? { gridColumn: `span ${Math.min(widgetCols, cols)}`, minHeight: `${minH}px` }
          // Desktop: honor stored col/row positions
          : {
              gridColumn: `${(w.col ?? 0) + 1} / span ${Math.min(widgetCols, cols - (w.col ?? 0))}`,
              gridRow:    `${(w.row ?? 0) + 1} / span ${rowSpan}`,
              minHeight:  `${minH}px`,
            }
        return (
          <div key={w.id} style={style} className="min-w-0">
            <div className="h-full border-2 border-[#358ECD]/30 rounded-lg overflow-hidden">
              <WidgetCell widget={w} isEditing={isEditing} onRemove={onRemove} onConfigure={onConfigure} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export const TemplateDashboardGrid = forwardRef(function TemplateDashboardGrid(
  { projectId, templateId: propTemplateId, isEditing = false, droppingWidget = null, onDropComplete, containerWidth: externalWidth = 0 },
  ref
) {
  // ── Data state ──
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState(null)
  const [layoutMeta, setLayoutMeta]     = useState(null)
  const [widgets, setWidgets]           = useState([])
  const [layoutConfig, setLayoutConfig] = useState({ cols: 3, row_height: 220, gap: 8 })

  // ── Template selector ──
  const [projectTemplates, setProjectTemplates] = useState([])
  const [activeTemplateId, setActiveTemplateId] = useState(propTemplateId || null)
  const [showSelector, setShowSelector]         = useState(false)

  // ── Layout state: base = last saved/loaded, gl = current (editable) ──
  const [baseWidgets, setBaseWidgets] = useState([])
  const [glItems, setGlItems]         = useState([])
  const [baseItems, setBaseItems]     = useState([])
  const [isDirty, setIsDirty]         = useState(false)
  const [saveStatus, setSaveStatus]   = useState(null) // null | 'saving' | 'saved'

  // ── Widget config modal ──
  const [configWidget, setConfigWidget] = useState(null)

  // Keep a ref so handleExternalDrop always reads the latest droppingWidget prop,
  // avoiding stale-closure issues when React hasn't flushed the re-render yet.
  const droppingWidgetRef = useRef(droppingWidget)
  useEffect(() => { droppingWidgetRef.current = droppingWidget }, [droppingWidget])

  // ── Container width for react-grid-layout ────────────────────────────────────
  // We own the ResizeObserver here (instead of using useContainerWidth from RGL)
  // because RGL's hook fires via requestAnimationFrame and can report a stale/narrow
  // width when sidebar CSS transitions are in flight at mount time, overwriting the
  // correct value measured after the transition settles.
  const rglContainerRef = useRef(null)
  const [rglWidth, setRglWidth]     = useState(0)
  const prevEditingRef  = useRef(isEditing)
  const editEnterRef    = useRef(false)   // true = skip RGL's initial onLayoutChange after entering edit

  // ── Load layout: user-specific first, fall back to template default ──
  const loadLayout = useCallback(async (templateId) => {
    if (!templateId) return
    setLoading(true)
    setError(null)
    setIsDirty(false)
    try {
      const templateRes = await dashboardApi.getTemplateLayout(templateId)
      const data = templateRes.data
      setLayoutMeta(data)
      const wList = Array.isArray(data.widgets) ? data.widgets : []
      const cfg = {
        cols:       data.layout_config?.cols       || 3,
        row_height: Math.max(data.layout_config?.row_height || 200, 150),
        gap:        data.layout_config?.gap        || 8,
      }
      setLayoutConfig(cfg)

      // ✅ Load cached dimensions from localStorage (primary source for sizing)
      // Check both new format (dashboard_widgets_*) and old format (dashboard_layout_*)
      let cachedWidgets = null
      let cachedLayoutMap = null
      
      if (projectId) {
        try {
          // Try new format first
          const newKey = `dashboard_widgets_${templateId}_${projectId}`
          let cached = localStorage.getItem(newKey)
          if (cached) {
            cachedWidgets = JSON.parse(cached)
          } else {
            // Try old format: dashboard_layout_14_2503 with structure {w1: {w, h, x, y}, w2: {...}, ...}
            const oldKey = `dashboard_layout_${templateId}_${projectId}`
            const oldCached = localStorage.getItem(oldKey)
            if (oldCached) {
              cachedLayoutMap = JSON.parse(oldCached)
              console.log('[TDG] Loaded old localStorage format:', cachedLayoutMap)
            }
          }
        } catch (e) {
          console.warn('[TDG] Failed to parse localStorage cache:', e)
        }
      }

      // Try user-specific saved layout
      if (projectId) {
        try {
          const userRes = await dashboardApi.getUserLayout(templateId, projectId)
          const ud = userRes.data
          if (ud.has_custom && Array.isArray(ud.layout) && ud.layout.length > 0) {
            const wMap = Object.fromEntries(wList.map(w => [String(w.id), w]))
            const userWidgets = ud.layout.map(uw => {
              const orig = wMap[String(uw.id)]
              const cached = cachedWidgets?.find(cw => String(cw.id) === String(uw.id))
              
              // Check old localStorage format: {w{id}: {w, h, x, y}}
              // Old format was for 12-column grid, only use if w value is 1, 2, or 3
              let oldCached = null
              if (cachedLayoutMap && cachedLayoutMap[`w${uw.id}`]) {
                const oldData = cachedLayoutMap[`w${uw.id}`]
                // Scale 12-column widths to 3-column: round(w * 3 / 12)
                const scaledCols = Math.round(oldData.w * 3 / 12)
                // Only use if it results in valid 3-column value (1-3)
                if (scaledCols >= 1 && scaledCols <= 3) {
                  oldCached = { cols: scaledCols, rows: oldData.h, col: 0, row: oldData.y }
                  console.log(`[TDG] Converted w${uw.id}: w=${oldData.w} → cols=${scaledCols}`)
                }
              }
              
              const base = orig
                ? { 
                    ...orig, 
                    col: uw.col ?? orig.col, 
                    row: uw.row ?? orig.row,
                    // ✅ Prioritize: backend cols → new localStorage cache → old localStorage cache (scaled) → original cols
                    cols: uw.cols && uw.cols > 1 
                      ? uw.cols 
                      : (cached?.cols ?? oldCached?.cols ?? orig.cols), 
                    rows: uw.rows && uw.rows > 1 
                      ? uw.rows 
                      : (cached?.rows ?? oldCached?.rows ?? orig.rows)
                  }
                : { ...uw }
              // Restore per-instance config overrides
              if (uw.custom_title !== undefined) base.custom_title = uw.custom_title
              if (uw.custom_description !== undefined) base.custom_description = uw.custom_description
              if (uw.custom_rich_content !== undefined) base.custom_rich_content = uw.custom_rich_content
              return base
            })
            setWidgets(userWidgets)
            setBaseWidgets(userWidgets)
            const gl = toGLItems(userWidgets, cfg.cols)
            setGlItems(gl)
            setBaseItems(gl)
            return
          }
        } catch (e) {
          console.error('[TDG] Error loading user layout:', e)
        }
      }

      // Template default
      setWidgets(wList)
      setBaseWidgets(wList)
      const gl = toGLItems(wList, cfg.cols)
      setGlItems(gl)
      setBaseItems(gl)
    } catch (err) {
      console.error('[TemplateDashboardGrid] load error:', err)
      setError('Failed to load template layout.')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  // ── Load project templates on mount ──
  useEffect(() => {
    if (!projectId) return
    // Strictly project-scoped — no fallback to getTemplates() (the
    // unfiltered endpoint), which would leak every project's custom
    // dashboards into the current project's grid on any transient error.
    dashboardApi.getProjectTemplates(projectId)
      .then(res => {
        const list = Array.isArray(res.data?.templates || res.data) ? (res.data?.templates || res.data) : []
        setProjectTemplates(list)
        if (!activeTemplateId && list.length) setActiveTemplateId(list[0].id)
      })
      .catch(err => {
        console.warn('[TDG] getProjectTemplates failed', err?.response?.status)
        setProjectTemplates([])
      })
  }, [projectId])

  // ── Load layout when activeTemplateId changes ──
  useEffect(() => {
    if (activeTemplateId) loadLayout(activeTemplateId)
  }, [activeTemplateId, loadLayout])

  const isEditingRef = useRef(isEditing)

  // ── Measure container width for GridLayout ──────────────────────────────────
  // Uses getBoundingClientRect (not offsetWidth) to get the true rendered width
  // after all flex/grid layout and CSS transitions have settled. A ResizeObserver
  // keeps rglWidth in sync when the container resizes (e.g. sidebar animation,
  // window resize, scrollbar appearance). Only active while in edit mode.
  useEffect(() => {
    isEditingRef.current = isEditing
    if (!isEditing) {
      setRglWidth(0)
      return
    }
    const node = rglContainerRef.current
    if (!node) return

    const measure = () => {
      const rect = node.getBoundingClientRect()
      const w = rect.width
      console.log('[TDG] rglContainerRef width:', w, 'rect:', rect, 'offsetWidth:', node.offsetWidth, 'parentOffsetWidth:', node.parentElement?.offsetWidth)
      if (w > 0) setRglWidth(Math.floor(w))
    }

    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [isEditing])

  // ── Sync glItems when entering edit mode ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEditing) return

    // Re-derive glItems fresh from widgets so the RGL edit grid matches the CSS-grid view.
    setGlItems(toGLItems(widgets, layoutConfig.cols))
    setIsDirty(false)

    // Guard flag: skip the very first onLayoutChange RGL fires on its initial render.
    editEnterRef.current = true
    const flagTimer = setTimeout(() => { editEnterRef.current = false }, 300)

    return () => { clearTimeout(flagTimer) }
  }, [isEditing]) // intentionally omit widgets/layoutConfig: read via closure at entry time

  // ── Revert unsaved changes when editing mode closes ──
  useEffect(() => {
    if (prevEditingRef.current && !isEditing && isDirty) {
      setWidgets(baseWidgets)
      setGlItems(baseItems)
      setIsDirty(false)
    }
    prevEditingRef.current = isEditing
  }, [isEditing, isDirty, baseWidgets, baseItems])

  // ── Handlers ──
  const handleLayoutChange = useCallback(next => {
    // Skip the first onLayoutChange that RGL fires on its initial render after entering
    // edit mode.  At that moment glItems was just set from `widgets` (the view-mode
    // layout), and any "normalization" RGL applies would produce a different layout from
    // what the user sees in view mode — the layout-mismatch bug.
    if (editEnterRef.current) {
      editEnterRef.current = false
      return
    }

    // Filter out the external-drop placeholder — it's a temporary RGL artefact
    // that gets replaced with the real widget id in handleExternalDrop.
    const filtered = next.filter(item => item.i !== '__drop__')

    setGlItems(prev => {
      const changed =
        filtered.length !== prev.length ||
        filtered.some(item => {
          const p = prev.find(i => i.i === item.i)
          return !p || p.x !== item.x || p.y !== item.y || p.w !== item.w || p.h !== item.h
        })
      if (!changed) return prev

      // ✅ SYNC: Update widgets array so CSS-grid view reflects resizes immediately
      // This ensures that when user resizes a widget from 1→2→3 columns, the view mode
      // displays the correct span without needing to save first.
      setWidgets(prevWidgets =>
        prevWidgets.map(w => {
          const glItem = filtered.find(i => i.i === String(w.id))
          if (!glItem) return w
          // Keep in sync with GridLayout positions
          return {
            ...w,
            col: glItem.x,
            row: glItem.y,
            cols: glItem.w,
            rows: glItem.h,
          }
        })
      )

      setIsDirty(true)
      return filtered
    })
  }, [])

  const handleRemoveWidget = useCallback(wId => {
    const sid = String(wId)
    setWidgets(prev => prev.filter(w => String(w.id) !== sid))
    setGlItems(prev => prev.filter(i => i.i !== sid))
    setIsDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!activeTemplateId || !projectId) return
    setSaveStatus('saving')
    const layout = widgets.map(w => {
      const gl = glItems.find(i => i.i === String(w.id))
      const item = {
        id: w.id,
        widget_type: w.widget_type,
        name: w.name,
        master_widget_id: w.master_widget_id || w.master_widget?.id,
        col:  gl ? gl.x : (w.col  ?? 0),
        row:  gl ? gl.y : (w.row  ?? 0),
        cols: gl ? gl.w : (w.cols ?? 1),
        rows: gl ? gl.h : (w.rows ?? 1),
        icon:  w.icon,
        color: w.color,
      }
      // Persist per-instance config overrides alongside layout positions
      if (w.custom_title !== undefined) item.custom_title = w.custom_title
      if (w.custom_description !== undefined) item.custom_description = w.custom_description
      if (w.custom_rich_content !== undefined) item.custom_rich_content = w.custom_rich_content
      return item
    })
    try {
      await dashboardApi.saveUserLayout(activeTemplateId, { project_id: projectId, layout })
      
      // ✅ Save complete widget data with cols/rows to localStorage as primary sizing backup
      // Since backend might not persist cols/rows, this ensures resizes are remembered
      const widgetsForCache = widgets.map(w => {
        const gl = glItems.find(i => i.i === String(w.id))
        return {
          id: w.id,
          cols: gl ? gl.w : (w.cols ?? 1),
          rows: gl ? gl.h : (w.rows ?? 1),
          col: gl ? gl.x : (w.col ?? 0),
          row: gl ? gl.y : (w.row ?? 0),
        }
      })
      const cacheKey = `dashboard_widgets_${activeTemplateId}_${projectId}`
      localStorage.setItem(cacheKey, JSON.stringify(widgetsForCache))
      console.log('[TDG] Saved widget cache to localStorage:', cacheKey, widgetsForCache)
      
      // Sync widgets state from glItems so CSS-grid view reflects the saved positions
      const savedWidgets = widgets.map(w => {
        const gl = glItems.find(i => i.i === String(w.id))
        return gl ? { ...w, col: gl.x, row: gl.y, cols: gl.w, rows: gl.h } : w
      })
      setWidgets(savedWidgets)
      setBaseWidgets(savedWidgets)
      setBaseItems([...glItems])
      setIsDirty(false)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus(null), 2000)
    } catch (e) {
      console.error('[TDG] Save failed:', e)
      setSaveStatus(null)
    }
  }, [activeTemplateId, projectId, widgets, glItems])

  const handleReset = useCallback(async () => {
    if (!activeTemplateId) return
    if (projectId) {
      try {
        await dashboardApi.resetUserLayout(activeTemplateId, { project_id: projectId })
      } catch {}
    }
    await loadLayout(activeTemplateId)
  }, [activeTemplateId, projectId, loadLayout])

  const handleAddWidget = useCallback((masterWidget) => {
    const newId = `wnew_${Date.now()}`
    const maxY  = glItems.reduce((max, i) => Math.max(max, i.y + i.h), 0)
    // Master widgets often come back with default_width=1 which is too small
    // to be useful (microscopic cells in a 3-4 col grid). Floor to 2x2 so the
    // newly-added widget is at least visible/usable.
    const newW  = Math.max(masterWidget.default_width  || 2, 2)
    const newH  = Math.max(masterWidget.default_height || 2, 2)
    console.log('[TDG] addWidget:', { newId, widget_type: masterWidget.widget_type, newW, newH, maxY })
    const newWidget = {
      id:               newId,
      widget_type:      masterWidget.widget_type,
      name:             masterWidget.name,
      master_widget_id: masterWidget.id,
      master_widget:    masterWidget,
      col:  0,
      row:  maxY,
      cols: newW,
      rows: newH,
      icon:  masterWidget.icon,
      color: masterWidget.color,
    }
    setWidgets(prev => [...prev, newWidget])
    setGlItems(prev => [...prev, { i: newId, x: 0, y: maxY, w: newW, h: newH, minW: 1, minH: 1 }])
    setIsDirty(true)
  }, [glItems])

  // Drop handler for widgets dragged from the "Add Widgets" popup onto the grid
  const handleExternalDrop = useCallback((layout, item) => {
    const dw = droppingWidgetRef.current  // use ref — state may not have flushed yet
    if (!dw) return
    const newId = `wnew_${Date.now()}`
    const newW = item.w || dw.default_width  || 2
    const newH = item.h || dw.default_height || 2
    const newWidget = {
      id:               newId,
      widget_type:      dw.widget_type,
      name:             dw.name,
      master_widget_id: dw.id,
      master_widget:    dw,
      col:  item.x,
      row:  item.y,
      cols: newW,
      rows: newH,
      icon:  dw.icon,
      color: dw.color,
    }
    setWidgets(prev => [...prev, newWidget])
    // Replace the temporary __drop__ placeholder id with the real widget id
    setGlItems(layout.map(i => i.i === '__drop__' ? { ...i, i: newId, minW: 1, minH: 1 } : i))
    setIsDirty(true)
    onDropComplete?.()
  }, [onDropComplete])

  const handleRefresh = useCallback(() => {
    if (activeTemplateId) loadLayout(activeTemplateId)
  }, [activeTemplateId, loadLayout])

  // ── Expose save / reset / addWidget via ref ──
  useImperativeHandle(ref, () => ({
    save:      handleSave,
    reset:     handleReset,
    addWidget: handleAddWidget,
  }), [handleSave, handleReset, handleAddWidget])

  const widgetMap = Object.fromEntries(widgets.map(w => [String(w.id), w]))
  const rowH = layoutConfig.row_height || 220
  const gap  = layoutConfig.gap || 8
  // Use parent-measured width if available (more reliable than internal measurement)
  const effectiveWidth = externalWidth || rglWidth
  if (isEditing) console.log('[TDG] effectiveWidth:', effectiveWidth, 'externalWidth:', externalWidth, 'rglWidth:', rglWidth)

  return (
    <div className="space-y-0">
      {/* Edit mode hint */}
      {isEditing && !droppingWidget && (
        <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-600">
          <Edit2 className="w-3.5 h-3.5 shrink-0" />
          Drag widgets to rearrange · Drag the bottom-right corner to resize · Click ✕ to remove · Click ✎ to edit
        </div>
      )}

      {/* Drop zone hint — shown while user is dragging a widget from the popup */}
      {isEditing && droppingWidget && (
        <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-green-50 border border-green-300 rounded-lg text-xs text-green-700 border-dashed">
          <span className="text-base">{droppingWidget.icon || '📦'}</span>
          Drop <strong>{droppingWidget.name}</strong> anywhere on the grid below
        </div>
      )}

      {/* ── Content area — rglContainerRef feeds useContainerWidth for accurate width ── */}
      <div ref={rglContainerRef} className="w-full">
        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader className="w-6 h-6 animate-spin mr-2" />
            <span className="text-sm">Loading template layout…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">{error}</p>
            <button onClick={handleRefresh} className="mt-3 text-xs text-blue-500 underline">Try again</button>
          </div>
        )}

        {!loading && !error && !activeTemplateId && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <LayoutGrid className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No template assigned to this project</p>
            <p className="text-xs mt-1">Assign a template in the admin dashboard</p>
          </div>
        )}

        {!loading && !error && activeTemplateId && widgets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-400">
            <LayoutGrid className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No widgets in this template</p>
            <p className="text-xs mt-1">
              <a href={`/admin/master-dashboard/template/${activeTemplateId}/layout/`} className="text-[#358ECD] underline" target="_blank" rel="noreferrer">
                Open Layout Editor →
              </a>
            </p>
          </div>
        )}

        {!loading && !error && widgets.length > 0 && (
          <>
            {/* EDIT MODE: Full Width Grid (same as view mode, just editable) */}
            {isEditing && effectiveWidth > 0 && (
              <GridLayout
                layout={glItems}
                cols={layoutConfig.cols || 3}
                rowHeight={rowH}
                width={effectiveWidth}
                margin={[gap, gap]}
                containerPadding={[0, 0]}
                compactType={null}
                preventCollision={false}
                isDraggable
                isResizable
                isDroppable={isEditing}
                droppingItem={droppingWidget ? {
                  i: '__drop__',
                  w: droppingWidget.default_width  || 2,
                  h: droppingWidget.default_height || 2,
                } : undefined}
                onDrop={handleExternalDrop}
                onLayoutChange={handleLayoutChange}
                draggableCancel=".no-drag"
                resizeHandles={['se']}
                className="rgl-editing"
              >
                {glItems.map(item => {
                  const widget = widgetMap[item.i]
                  if (!widget) return null
                  return (
                    <div key={item.i} className="cursor-grab active:cursor-grabbing">
                      <div className="h-full border-2 border-[#358ECD]/30 rounded-lg overflow-hidden">
                        <WidgetCell widget={widget} isEditing onRemove={handleRemoveWidget} onConfigure={setConfigWidget} />
                      </div>
                    </div>
                  )
                })}
              </GridLayout>
            )}

            {/* VIEW MODE: CSS grid */}
            {!isEditing && (
              <CSSGridView
                widgets={widgets}
                layoutConfig={layoutConfig}
                isEditing={false}
                onRemove={() => {}}
                onConfigure={setConfigWidget}
              />
            )}
          </>
        )}
      </div>

      {/* Backdrop for template selector */}
      {showSelector && (
        <div className="fixed inset-0 z-40" onClick={() => setShowSelector(false)} />
      )}

      {/* Widget config modal */}
      {configWidget && (
        <WidgetConfigModal
          widget={configWidget}
          onClose={() => setConfigWidget(null)}
          onSaved={async (updates) => {
            // Apply updates locally
            const updated = widgets.map(w => {
              if (String(w.id) !== String(updates.instance_id)) return w
              return {
                ...w,
                custom_title: updates.title,
                custom_description: updates.description,
                custom_rich_content: updates.rich_content,
              }
            })
            setWidgets(updated)
            setConfigWidget(null)

            // Auto-save to backend immediately — no "Save Customization" click required
            if (activeTemplateId && projectId) {
              const layout = updated.map(w => {
                const gl = glItems.find(i => i.i === String(w.id))
                const item = {
                  id: w.id,
                  widget_type: w.widget_type,
                  name: w.name,
                  master_widget_id: w.master_widget_id || w.master_widget?.id,
                  col:  gl ? gl.x : (w.col  ?? 0),
                  row:  gl ? gl.y : (w.row  ?? 0),
                  cols: gl ? gl.w : (w.cols ?? 1),
                  rows: gl ? gl.h : (w.rows ?? 1),
                  icon:  w.icon,
                  color: w.color,
                }
                if (w.custom_title       !== undefined) item.custom_title       = w.custom_title
                if (w.custom_description !== undefined) item.custom_description = w.custom_description
                if (w.custom_rich_content !== undefined) item.custom_rich_content = w.custom_rich_content
                return item
              })
              try {
                await dashboardApi.saveUserLayout(activeTemplateId, { project_id: projectId, layout })
                setBaseWidgets(updated)
                setIsDirty(false)
              } catch (e) {
                console.error('[TDG] Content auto-save failed:', e)
                // Leave isDirty=true so the user still sees the Save button
                setIsDirty(true)
              }
            }
          }}
        />
      )}
    </div>
  )
})

export default TemplateDashboardGrid
